/* ════════════════════════════════════════════════════
 *  <kik-swiper> — reusable Swiper slider web component.
 *
 *  Wraps the global Swiper build (assets/kik-swiper.js → window.Swiper).
 *  Loaded per-section via component-kik-swiper.js.
 *
 *  Configuration is a child <script type="application/json"> — chosen over
 *  data-* attributes because Swiper options are deeply nested (navigation,
 *  pagination, breakpoints, autoplay) and flat attributes can't express
 *  that. The JSON maps 1:1 to Swiper's options object, so any Swiper option
 *  works with no change to this component.
 *
 *  Usage:
 *    <kik-swiper>
 *      <script type="application/json">
 *        {
 *          "slidesPerView": 1,
 *          "spaceBetween": 16,
 *          "loop": true,
 *          "pagination": { "el": ".swiper-pagination", "clickable": true },
 *          "navigation": { "nextEl": ".swiper-button-next", "prevEl": ".swiper-button-prev" },
 *          "breakpoints": { "750": { "slidesPerView": 2 }, "990": { "slidesPerView": 3 } }
 *        }
 *      </script>
 *      <div class="swiper">
 *        <div class="swiper-wrapper">
 *          <div class="swiper-slide">…</div>
 *        </div>
 *        <div class="swiper-pagination"></div>
 *        <button class="swiper-button-prev" type="button"></button>
 *        <button class="swiper-button-next" type="button"></button>
 *      </div>
 *    </kik-swiper>
 *
 *  All bundled modules (Navigation, Pagination, Autoplay) are passed every
 *  time — a module stays inert unless its config key is present. To use a
 *  module not in the build, add it to src/js/kik-swiper.js and rebuild.
 *
 *  The live Swiper instance is exposed as `element.swiper`.
 * ════════════════════════════════════════════════════ */

if (!customElements.get('kik-swiper')) {
  class KikSwiper extends HTMLElement {
    connectedCallback() {
      if (this.swiper) return; // guard a repeat connect on the same instance

      const container = this.querySelector('.swiper');
      if (!container) return;

      if (!window.Swiper) {
        console.warn('[kik-swiper] window.Swiper missing — is kik-swiper.js loaded?');
        return;
      }

      this.swiper = new window.Swiper(container, {
        modules: Object.values(window.SwiperModules || {}),
        ...this.#readConfig(),
      });

      this.container = container;
      container.addEventListener('scroll', this.#onScroll);
      this.addEventListener('focusin', this.#onFocusIn);
    }

    disconnectedCallback() {
      this.container?.removeEventListener('scroll', this.#onScroll);
      this.removeEventListener('focusin', this.#onFocusIn);
      this.container = null;
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    /* `overflow: hidden` stops the USER scrolling the track — it does not stop
       the BROWSER. Tabbing to a link inside a slide that is clipped or off-view
       makes it scroll the track to reveal that link, and because Swiper
       positions slides with a transform it never learns about the offset. The
       two desync and every slide is left misaligned, permanently.

       Applies to any slider whose slides overflow the track — a peek, a bleed,
       or simply more slides than fit. Reproduced at scrollLeft 361px with the
       transform still at 0. #onFocusIn moves the carousel properly instead. */
    #onScroll = () => {
      if (this.container.scrollLeft !== 0) this.container.scrollLeft = 0;
      if (this.container.scrollTop !== 0) this.container.scrollTop = 0;
    };

    /* Focus a card that is clipped or off-view and the track brings it into
       view — the keyboard counterpart to dragging or the nav arrows. Without
       this, tab order walks through slides the user cannot see. */
    #onFocusIn = (event) => {
      if (!this.swiper || !this.container) return;

      const slide = event.target.closest?.('.swiper-slide');
      if (!slide || !this.contains(slide)) return;

      const track = this.container.getBoundingClientRect();
      const box = slide.getBoundingClientRect();
      if (box.left >= track.left - 1 && box.right <= track.right + 1) return; // already fully visible

      const index = this.swiper.slides.indexOf(slide);
      if (index >= 0) this.swiper.slideTo(index);
    };

    /* Parse the child <script type="application/json"> options block.
       Missing or malformed → {} (Swiper runs with its own defaults). */
    #readConfig() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('[kik-swiper] invalid JSON config — using defaults.', error);
        return {};
      }
    }
  }

  customElements.define('kik-swiper', KikSwiper);
}
